import React, { useState } from 'react';
import {
  ArrowRightOutlined,
  CheckOutlined,
  FileExcelOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Brand from '../components/Brand';
import SupportButton from '../components/SupportButton';
import { useAuth } from '../contexts/AuthContext';
import './HomePage.css';

const examples = [
  {
    order: 'DEMO-10428',
    store: '北岸店铺',
    state: '待审核',
    tone: 'pending',
    amount: '38.60',
  },
  {
    order: 'DEMO-10427',
    store: '远山店铺',
    state: '待拍单',
    tone: 'active',
    amount: '124.90',
  },
  {
    order: 'DEMO-10426',
    store: '北岸店铺',
    state: '已拍单',
    tone: 'success',
    amount: '67.25',
  },
  {
    order: 'DEMO-10425',
    store: '远山店铺',
    state: '待审核',
    tone: 'pending',
    amount: '29.80',
  },
];
const steps = [
  [
    '01',
    '导入采购清单',
    '使用订单模板导入 Excel，核对文件处理结果，再进入订单列表。',
    'Excel 文件导入',
  ],
  [
    '02',
    '审核与批量处理',
    '按店铺、编号和订单状态缩小范围。处理前确认订单数量与积分消耗。',
    '筛选 · 审核 · 批量处理',
  ],
  [
    '03',
    '核对订单与积分',
    '查看商品、收货与物流明细，按筛选结果导出订单，结合积分流水完成核对。',
    '订单详情 / 导出 / 积分流水',
  ],
];

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const [previewFilter, setPreviewFilter] = useState('全部订单');
  const destination = isAuthenticated ? '/app/orders' : '/login';
  const rows = examples.filter(
    (row) => previewFilter === '全部订单' || row.state === previewFilter,
  );

  return (
    <div className="product-site">
      <a className="skip-link" href="#site-main">
        跳转到内容
      </a>
      <header className="site-nav site-width">
        <Brand />
        <nav aria-label="网站导航">
          <a href="#workflow">工作流程</a>
          <Link to={destination}>
            {isAuthenticated ? '进入工作台' : '客户登录'} <ArrowRightOutlined />
          </Link>
        </nav>
      </header>
      <main id="site-main">
        <section className="site-hero site-width">
          <div className="hero-copy">
            <p className="product-category">大黄蜂 · 跨境采购协作</p>
            <h1>
              Amazon 采购
              <br />
              订单协作工作台
            </h1>
            <p className="hero-intro">
              导入清单、审核订单、跟进物流。<br />
              把采购处理和明细核对放在同一个工作区。
            </p>
            <p className="hero-audience">适用于采购团队、代采服务商及固定合作客户</p>
            <div className="hero-links">
              <Link className="site-primary-link" to={destination}>
                {isAuthenticated ? '进入工作台' : '登录工作台'} <ArrowRightOutlined />
              </Link>
              <Link to="/register">受邀开通账号</Link>
            </div>
            <div className="hero-note">
              通过业务对接人获取邀请码，即可开通工作区。
            </div>
          </div>
          <div
            className="product-preview"
            aria-label="订单工作台示意，所有订单均为演示数据"
          >
            <div className="preview-top">
              <span className="preview-wordmark">大黄蜂 / 订单工作台</span>
              <span className="demo-label">演示数据</span>
            </div>
            <div className="preview-body">
              <div className="preview-heading">
                <div>
                  <h2>订单处理队列</h2>
                  <p className="preview-description">按状态筛选，核对每笔订单的处理进展。</p>
                </div>
                <FileExcelOutlined />
              </div>
              <div
                className="preview-tabs"
                role="group"
                aria-label="筛选演示订单"
              >
                {['全部订单', '待审核', '已拍单'].map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={previewFilter === value}
                    onClick={() => setPreviewFilter(value)}
                  >
                    {value}
                  </button>
                ))}
                <FilterOutlined aria-hidden="true" />
              </div>
              <div className="preview-table-wrap">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>订单 / 店铺</th>
                      <th>处理状态</th>
                      <th>金额 USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.order}>
                        <td>
                          <strong className="order-number">{row.order}</strong>
                          <small>{row.store}</small>
                        </td>
                        <td>
                          <span className={'order-status ' + row.tone}>
                            {row.state}
                          </span>
                        </td>
                        <td className="numeric">{row.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="preview-bottom">
                <span>
                  <CheckOutlined /> 查询、处理、核对
                </span>
                <span>流程示意，非实时业务数据</span>
              </div>
            </div>
          </div>
        </section>
        <section className="site-workflow site-width" id="workflow">
          <div className="workflow-heading">
            <p className="product-category">业务流程</p>
            <h2>
              从导入到核对，
              <br />
              按业务顺序处理。
            </h2>
            <p>
              保留团队熟悉的订单表，<br />
              在工作台完成后续处理和查询。
            </p>
          </div>
          <div className="workflow-steps">
            {steps.map(([number, title, description, caption]) => (
              <article key={number} className="workflow-step">
                <span className="step-number">{number}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                  <span className="step-caption">{caption}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="site-access site-width">
          <div>
            <p className="product-category">客户接入</p>
            <h2>准备好订单表，即可开始。</h2>
            <p>已有账号可直接登录。首次使用请联系业务对接人，获取邀请码与订单模板。</p>
          </div>
          <Link className="site-secondary-link" to="/register">
            受邀开通 <ArrowRightOutlined />
          </Link>
        </section>
      </main>
      <footer className="site-footer site-width">
        <span>大黄蜂采购工作台</span>
        <span>订单处理 / 物流查询 / 积分核对</span>
        <SupportButton type="link" size="small" />
        <Link to={destination}>客户入口</Link>
      </footer>
    </div>
  );
};
export default HomePage;
